/**
 * Карточка вишлиста с сеткой фотографий для страницы списка вишлистов
 * 
 * Отображает:
 * - Сетку из 1-3 фотографий желаний (1 большая сверху + 2 маленькие снизу)
 * - Бейдж с количеством желаний справа сверху
 * - Иконку, название и дату события под карточкой
 * - Пустую карточку если нет желаний
 */

import { memo, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { WISHLIST_ICONS } from '@/config';
import { useTranslation } from 'react-i18next';
import { WishlistPhotoGrid } from '@/components';
import type { Wishlist } from '../../model';
import { WishlistInfo } from './WishlistInfo';

interface WishlistGridCardProps {
  /** Данные вишлиста */
  wishlist: Wishlist;
  /** Обработчик клика */
  onClick?: () => void;
  /** Добавлен ли вишлист в избранное текущим пользователем */
  isFavorite?: boolean;
}

export const WishlistGridCard = memo(function WishlistGridCard({ wishlist, onClick, isFavorite = false }: WishlistGridCardProps): JSX.Element {
  const { t } = useTranslation();

  // Получаем фотографии желаний (максимум 4 для обложки)
  const wishPhotos = useMemo(() => {
    if (!wishlist.items) return [];
    return wishlist.items
      .filter(item => item.imageUrl)
      .slice(0, 4)
      .map(item => item.imageUrl) as string[]; // filter гарантирует non-null, но TS не сужает тип через callback
  }, [wishlist.items]);
  
  // Находим иконку по iconId
  const iconData = wishlist.iconId ? WISHLIST_ICONS.find(icon => icon.id === wishlist.iconId) : null;

  const itemCount = wishlist.itemCount ?? 0;

  return (
    <button type="button" onClick={onClick} className="text-left w-full">
      {/* Карточка с фотографиями */}
      <Card className="overflow-hidden active:scale-95 transition-transform shadow-[var(--shadow)] p-0">
        {/* Сетка фотографий или пустое состояние */}
        <WishlistPhotoGrid 
          wishPhotos={wishPhotos}
          emptyText={t('wishlist:wishlist.emptyWishlist.text')}
          itemCount={itemCount}
          isFavorite={isFavorite}
        />
      </Card>

      {/* Информация о вишлисте под карточкой */}
      <WishlistInfo 
        title={wishlist.title}
        eventDate={wishlist.eventDate}
        iconEmoji={iconData?.emoji}
      />
    </button>
  );
});